import React, { FC, useState } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Dropdown, SelectPicker } from 'rsuite';
import { Title } from '../developers/page-components/Title';
import { ApplicationReviewRiskModal } from './ApplicationReviewRiskModal';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../store/merchnts/merchants-context';
import clsx from 'clsx';
import { DropdownDisabledPlaceholder } from '../DropdownDisabledPlaceholder';
import { setApplicationRiskCategory } from '../../store/merchnts/actions/application-details';
import { useParams } from 'react-router-dom';
import { getListItemValue } from '../../utils/utilities';
import { STATUSES } from '../../views/login/Login';

export const ReviewUnderwriting: FC = () => {
  const { t } = useTranslation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const { merchantState: { merchantsLists, applicationData }, dispatch } = useMerchants();

  const [riskCategory, setRiskCategory] = React.useState<string>(applicationData?.riskCategory || '');
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

  const handleSelect = ({ name, status }): void => {
    setModalState({
      isOpen: true,
      modalTitle: 'Update application status',
      backdrop: 'static',
      contentClassName: 'd-block',
      minHeight: 100,
      content: <ApplicationReviewRiskModal name={name} status={status} onClose={closeModal} riskCategory={riskCategory} />
    });
  };

  const handleChange = async (value: string): Promise<void> => {
    setRiskCategory(value);
    await setApplicationRiskCategory({ dispatch, applicationId, riskCategory: value });
  };

  const riskStatus = getListItemValue(applicationData?.riskStatus, merchantsLists?.applicationRiskStatuses);

  const isDisabled = !applicationData?.primaryContact?.confirmedUser ||
    applicationData?.statusName?.toUpperCase() === STATUSES.CANCELLED ||
    applicationData?.statusName?.toUpperCase() === STATUSES.DECLINED;

  return (
    <SectionWrapper
      title={t('APPLICATION_DETAILS.LABEL.UNDERWRITING_SECTION')}
      sectionAdditionalClass="mb-5 min-height-200"
      loading={false}
      isCardBody
    >
      <div className="d-xl-flex justify-content-between">
        <div className="col-xl-7 mb-5 mb-xl-0">
          <div className="input-container w-250px">
            <label className="form-label mb-2">
              {t('APPLICATION_DETAILS.LABEL.RISK_CATEGORY')}
            </label>

            <SelectPicker
              className="p-0 select-picker"
              placeholder={t('APPLICATION_DETAILS.LABEL.RISK_CATEGORY')}
              data={merchantsLists?.riskCategories || []}
              value={riskCategory}
              onChange={handleChange}
              cleanable={false}
              searchable={false}
              disabled={isDisabled}
            />
          </div>

          <div className="mt-5">{t('APPLICATION_DETAILS.LABEL.UNDERWRITING_SECTION_TEXT')}</div>
        </div>

        <div className="save-btn-section">
          <Title title={t('APPLICATION_DETAILS.LABEL.UNDERWRITING_STATUS')} additionalClass="mb-2" />

          {
            isDisabled
              ? <DropdownDisabledPlaceholder name={riskStatus || t('APPLICATION_DETAILS.LABEL.UNDERWRITING_SECTION')} />
              : (
                <Dropdown
                  title={riskStatus || t('APPLICATION_DETAILS.LABEL.UNDERWRITING_SECTION')}
                  placement="bottomEnd"
                  data-cy="underwriting-status-dropdown"
                >
                  {
                    merchantsLists?.applicationRiskStatuses.map(({ value, label }) => {
                      return (
                        <Dropdown.Item
                          key={value}
                          onClick={(): void => handleSelect({ name: label, status: value })}
                          className={clsx('fw-bolder fs-7', applicationData?.riskStatus === value ? 'text-black main-font-family' : 'text-muted')}
                        >
                          {label}
                        </Dropdown.Item>
                      );
                    })
                  }
                </Dropdown>
              )
          }
        </div>
      </div>

      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />

    </SectionWrapper>
  );
};